import * as React from 'react'
import { Stack, Checkbox, Typography, Chip, Skeleton } from '@mui/material'
import { ControllerFieldState, ControllerRenderProps } from 'react-hook-form'

import { Select, Flex } from '@clara/ui'
import { Dict, isEmpty } from '@clara/utils'
import { useT } from '@clara/locale'

import { getErrorHelperText } from '@/utils/form'
import { DropdownOption } from '@/types/api'
import { useSnackbar } from '@/utils/use-snackbar'

interface LabelsInputProps {
  field: ControllerRenderProps<any, 'labels'>
  fieldState: ControllerFieldState
  labels: Array<DropdownOption & { canDelete?: boolean }>
  labelsDict: Dict<DropdownOption & { canDelete?: boolean }>
  isLoading: boolean
  maxValues?: number
  isClearable?: boolean
}

const widthOptions = [80, 100, 60, 130]

export const LabelsInput: React.FC<LabelsInputProps> = ({
  field,
  fieldState,
  labels,
  labelsDict,
  isLoading,
  maxValues = 7,
  isClearable = false,
}) => {
  const { t } = useT('reimbursements')
  const showSnackbar = useSnackbar()
  return (
    <Stack>
      <Select
        placeholder=""
        isLoading={isLoading}
        label={t('create.request.form.labels')}
        options={labels}
        value={labels.filter((l) => field.value?.includes(l.value))}
        onChange={(newValue) => {
          if (newValue.length <= maxValues) {
            field.onChange(newValue.map(({ value }) => value))
          } else {
            showSnackbar(t('create.request.form.labels.maxvalue'), {
              variant: 'error',
            })
          }
        }}
        error={!!fieldState.error}
        helperText={getErrorHelperText(t, fieldState.error)}
        isMulti={true}
        maxRenderValues={0}
        isOptionDisabled={(option) => option.canDelete === false}
        isClearable={isClearable}
        renderOption={(props) => (
          <Stack direction="row" alignItems="center">
            <Checkbox
              size="small"
              checked={props.isSelected}
              aria-label={props.label}
              disabled={props.data.canDelete === false}
              color="primary"
            />
            <Typography component="span" flex={1} variant="body2">
              {props.label}
            </Typography>
          </Stack>
        )}
      />
      {isLoading ? (
        <Flex direction="row" gap={1} mt={1} flexWrap={'wrap'}>
          {field.value?.map((_: any, index: number) => {
            return (
              <Skeleton
                key={`skeleton-${index}`}
                variant="rounded"
                width={
                  widthOptions[Math.floor(Math.random() * widthOptions.length)]
                }
                height={32}
                sx={{ borderRadius: '16px' }}
              />
            )
          })}
        </Flex>
      ) : (
        <Flex
          sx={{
            mt: isEmpty(field.value) ? 0 : 1,
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          {field.value?.map((labelUuid: string) =>
            labelUuid ? (
              <Chip
                key={`label-chip-${labelUuid}`}
                label={labelsDict[labelUuid]?.label}
                color={labelsDict[labelUuid]?.canDelete ? 'info' : 'neutral'}
                onDelete={
                  labelsDict[labelUuid]?.canDelete
                    ? (_) =>
                        field.onChange(
                          field.value?.filter((v: string) => v !== labelUuid),
                        )
                    : undefined
                }
              />
            ) : null,
          )}
        </Flex>
      )}
    </Stack>
  )
}
